import { FC, useEffect, useState } from 'react'
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Grid,
  InputAdornment,
  makeStyles,
  MuiStack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@perk-ui/core'
import clsx from 'clsx'
import { addDays, addMonths, addWeeks, addYears, format } from 'date-fns'
import { FieldArray, FormikProvider, useFormik } from 'formik'
import { matchSorter } from 'match-sorter'
import { Search } from 'react-feather'
import { useRouteMatch } from 'react-router'
import * as yup from 'yup'

import { useSitesContext } from '../../../components/SitesProvider'
import useAnatomicalLocations, {
  AnatomicalLocation,
} from '../../../features/query-hooks/useAnatomicalLocations'
import useCreateCase from '../../../features/query-hooks/useCreateCase'
import useCreateDiagnosis, {
  PatientDiagnosisSeverity,
} from '../../../features/query-hooks/useCreateDiagnosis'
import useCreateTreatment from '../../../features/query-hooks/useCreateTreatment'
import usePatientCases from '../../../features/query-hooks/usePatientCases'
import useProtocols, {
  Protocol,
} from '../../../features/query-hooks/useProtocols'
import useProviders from '../../../features/query-hooks/useProviders'
import useSpecialtyBodySites, {
  BodySite,
} from '../../../features/query-hooks/useSpecialtyBodySites'
import useStudies, { Study } from '../../../features/query-hooks/useStudies'
import { buildName } from '../../../utils/buildName'
import { useHistory } from '../../../utils/useHistory'
import PatientSelfOnboardUrls, {
  PatientSelfOnboardParams,
} from '../PatientSelfOnboardUrls'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '787px',
    width: '100%',
    height: '100%',
  },
  form: {
    marginTop: theme.spacing(2),
    '& > *:not(:last-child)': {
      paddingBottom: theme.spacing(2),
    },
  },
  searchField: {
    paddingLeft: 0,
    width: '100%',
  },
  searchIcon: {
    marginLeft: theme.spacing(1.5),
    width: theme.spacing(4),
    marginRight: theme.spacing(-2.5),
    color: theme.palette.grey[600],
  },
  title: {
    fontFamily: 'DM Sans',
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: '24px',
    textAlign: 'center',
    marginBottom: '40px',
  },
  bodyPartCard: {
    width: '184px',
    height: '151px',
    backgroundColor: '#F8F8F8',
    borderRadius: '17px',
    cursor: 'pointer',
  },
  bodyPartText: {
    fontFamily: 'DM Sans',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '23.44px',
    textAlign: 'center',
    color: '#182C5A',
  },
  avatar: {
    backgroundColor: '#E3F2FD',
    width: '69px',
    height: '69px',
    marginTop: '20px',
    marginBottom: '10px',
  },
  avatarIcon: {
    width: '40px',
    height: '40px',
    color: '#42A5F5',
  },
  activeBodyPartCard: {
    border: '2px solid #56AAE8',
  },
  activeBodyPartText: {
    color: '#56AAE8',
  },
  goBackButton: {
    border: '1px solid #90CAF9', // Light blue border
    borderRadius: '8px', // Rounded corners
    color: '#42A5F5 !important', // Blue text color
    textColor: '#42A5F5', // Blue text color
    textTransform: 'none', // Prevents all-uppercase text
    // fontWeight: 500, // Semi-bold text
    backgroundColor: 'white', // White background
    '&:hover': {
      backgroundColor: '#E3F2FD', // Light blue background on hover
    },
  },
  disabledBodyPartCard: {
    pointerEvents: 'none',
  },
}))

interface AddCaseFormValues {
  bodySiteIds: Array<string>
  protocol?: Protocol | null
  anticipatedTreatmentDate: string
  // patientStudyNumber: string
  enrollment: string
  providerId?: string
  studyId: string
}

export interface AddCaseProps {}

const AddCase: FC<AddCaseProps> = () => {
  const classes = useStyles()
  const createCase = useCreateCase()
  const { push, goBack } = useHistory()
  const [availableBodySitesOptions, setAvailableBodySitesOptions] = useState<
    BodySite[]
  >([])
  const [searchValue, setSearchValue] = useState('')
  const patientIdMatch = useRouteMatch<PatientSelfOnboardParams>(
    PatientSelfOnboardUrls.addCase.match,
  )

  const patientId = patientIdMatch?.params.patientId
  const { currentSite } = useSitesContext()
  const { data: studies = [] } = useStudies()
  const { data: bodySites = [] } = useSpecialtyBodySites()
  const { data: providers = [] } = useProviders()
  const { data: patientCases = [] } = usePatientCases(patientId)
  const { data: protocols } = useProtocols('FullProtocol')
  const createDiagnosis = useCreateDiagnosis()
  const createTreatment = useCreateTreatment()

  const isContura = currentSite?.organization?.name === 'Contura'
  const noneStudy = studies.find((s) => s.slug === 'none')
  const conturaStudy = studies.find((s) => s.slug === 'arthrosamid-knee-oa')

  const availableStudies = (studies || []).filter(
    (study) => study.slug !== 'none',
  )

  const [
    protocolAnatomicalLocations,
    setProtocolAnatomicalLocations,
  ] = useState<AnatomicalLocation[]>([])

  const validationSchema = yup.object({
    bodySiteIds: yup
      .array()
      .of(yup.string())
      .min(1, 'Please select a body part'),
  })

  const usedBodySites = patientCases
    .filter((pc) => pc.status === 'active')
    .map((pc) => pc.bodySiteId)
  const availableBodySites = bodySites.filter(
    (site) => !usedBodySites.includes(site.id),
  )

  const updateBodySitesOptions = (
    study: {
      id: string
      slug: string
      bodySites: { id: string; slug: string }[]
    } | null,
  ) => {
    if (study && study.slug != 'none') {
      const selectedStudy = studies.find((s) => s.id === study.id)
      if (selectedStudy) {
        const filteredAvailableBodySites = availableBodySites.filter(
          (site: BodySite) =>
            selectedStudy.bodySites.map((bs) => bs.slug).includes(site.slug),
        )
        setAvailableBodySitesOptions(filteredAvailableBodySites)
        if (selectedStudy.bodySites.length === 1) {
          setFieldValue('bodySiteIds', [selectedStudy.bodySites[0].id])
        }
      }
    } else {
      const filteredBodySites = availableBodySites.filter(
        (bs) => bs.onlyForStudy == false,
      )
      setAvailableBodySitesOptions(filteredBodySites)
    }
  }
  useEffect(() => {
    const filteredBodySites = availableBodySites.filter(
      (bs) => bs.onlyForStudy == false,
    )
    setAvailableBodySitesOptions(filteredBodySites)

    if (filteredBodySites.length === 1) {
      setFieldValue('bodySiteIds', [filteredBodySites[0].id])
    }
  }, [bodySites])
  useEffect(() => {
    setAvailableBodySitesOptions(
      matchSorter(availableBodySites, searchValue, { keys: ['name'] }),
    )
  }, [searchValue])

  const formik = useFormik<AddCaseFormValues>({
    validationSchema,
    initialValues: {
      bodySiteIds: [],
      anticipatedTreatmentDate: format(new Date(), 'yyyy-MM-dd'),
      // patientStudyNumber: '',
      enrollment: 'standard',
      protocol: null,
      studyId: '',
      providerId: '',
    },
    onSubmit: (values) => {
      if (!patientId) return
      const mmddyyyyFormat = format(new Date(), 'MM-dd-yyyy')
      values.bodySiteIds.map((bodySiteId) => {
        // Determine studyId based on user selection
        let studyId: string
        if (values.enrollment === 'standard') {
          // Standard care always uses 'none' study
          studyId = noneStudy?.id || ''
        } else if (values.enrollment === 'study') {
          // Study/protocol enrollment: use user's selection or fallback
          if (values.studyId) {
            // User selected a specific study
            studyId = values.studyId
          } else if (values.protocol) {
            // User selected a protocol - protocols may not have associated studies
            studyId = ''
          } else {
            // Fallback for backwards compatibility
            studyId = isContura ? conturaStudy?.id || '' : noneStudy?.id || ''
          }
        } else {
          // Fallback for backwards compatibility
          studyId = isContura ? conturaStudy?.id || '' : noneStudy?.id || ''
        }
        values.studyId = studyId
        return createCase
          .mutateAsync({
            patientId: patientId,
            bodySiteId,
            protocolId: values.protocol?.id,
            ...values,
          })
          .then(async (pCase) => {
            if (values.protocol) {
              createDiagnosis.mutateAsync({
                patientCaseId: pCase.id,
                protocolId: values.protocol?.id,
                medicalConditionId:
                  (values.protocol?.protocolDiagnoses || [])[0]
                    .medicalConditionId || '',
                severity: (values.protocol?.protocolDiagnoses || [])[0]
                  .severity as PatientDiagnosisSeverity,
                isPrimary: (values.protocol?.protocolDiagnoses || [])[0]
                  .isPrimary,
                locations: protocolAnatomicalLocations,
                patientId: patientId,
              })

              const protocolPrimaryTreatments =
                values.protocol?.protocolTreatments || []
              if (protocolPrimaryTreatments) {
                const sortedTreatments = protocolPrimaryTreatments.sort()
                for (const treatment of sortedTreatments) {
                  let unformattedTreatmentDate
                  switch (treatment.periodType) {
                    case 'days':
                      unformattedTreatmentDate = addDays(
                        new Date(mmddyyyyFormat),
                        treatment.periodAmount || 0,
                      )
                      break
                    case 'weeks':
                      unformattedTreatmentDate = addWeeks(
                        new Date(mmddyyyyFormat),
                        treatment.periodAmount || 0,
                      )
                      break
                    case 'months':
                      unformattedTreatmentDate = addMonths(
                        new Date(mmddyyyyFormat),
                        treatment.periodAmount || 0,
                      )
                      break
                    case 'years':
                      unformattedTreatmentDate = addYears(
                        new Date(mmddyyyyFormat),
                        treatment.periodAmount || 0,
                      )
                      break

                    default:
                      unformattedTreatmentDate = addWeeks(
                        new Date(mmddyyyyFormat),
                        0,
                      )
                      break
                  }
                  createTreatment.mutateAsync({
                    patientCaseId: pCase.id,
                    siteLocationId: treatment.siteLocationId || '',
                    providerId: treatment.staffId || '',
                    medicalTreatmentId: treatment.medicalTreatmentId,
                    treatmentProductId: treatment.treatmentProductId,
                    protocolId: values.protocol?.id,
                    treatmentDate: format(
                      unformattedTreatmentDate as Date,
                      'yyyy-MM-dd',
                    ),
                  })
                }
              }
              push(
                PatientSelfOnboardUrls.addTreatment.create(patientId, pCase.id),
              )
            } else {
              if (values.enrollment === 'study' && values.studyId) {
                const studyToUse = studies.find((s) => s.id === values.studyId)
                if (studyToUse && studyToUse.protocol) {
                  const studyProtocolPrimaryTreatments =
                    studyToUse?.protocol?.protocolTreatments || []
                  if (studyProtocolPrimaryTreatments) {
                    const sortedTreatments = studyProtocolPrimaryTreatments.sort()
                    for (const treatment of sortedTreatments) {
                      let unformattedTreatmentDate
                      switch (treatment.periodType) {
                        case 'days':
                          unformattedTreatmentDate = addDays(
                            new Date(values.anticipatedTreatmentDate),
                            treatment.periodAmount || 0,
                          )
                          break
                        case 'weeks':
                          unformattedTreatmentDate = addWeeks(
                            new Date(values.anticipatedTreatmentDate),
                            treatment.periodAmount || 0,
                          )
                          break
                        case 'months':
                          unformattedTreatmentDate = addMonths(
                            new Date(values.anticipatedTreatmentDate),
                            treatment.periodAmount || 0,
                          )
                          break
                        case 'years':
                          unformattedTreatmentDate = addYears(
                            new Date(values.anticipatedTreatmentDate),
                            treatment.periodAmount || 0,
                          )
                          break

                        default:
                          unformattedTreatmentDate = addWeeks(
                            new Date(values.anticipatedTreatmentDate),
                            0,
                          )
                          break
                      }
                      await createTreatment.mutateAsync({
                        patientCaseId: pCase.id,
                        siteLocationId: treatment.siteLocationId || '',
                        providerId:
                          values.providerId || treatment.staffId || '',
                        medicalTreatmentId: treatment.medicalTreatmentId,
                        treatmentProductId: treatment.treatmentProductId,
                        protocolId: studyToUse.protocol?.id,
                        treatmentDate: format(
                          unformattedTreatmentDate as Date,
                          'yyyy-MM-dd',
                        ),
                      })
                    }
                  }
                }
              }
              push(
                PatientSelfOnboardUrls.addDiagnoses.create(patientId, pCase.id),
              )
            }
          })
      })
    },
  })

  const {
    values,
    isValid,
    errors,
    touched,
    // handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setFieldError,
    submitForm,
  } = formik

  const { data: anatomicalLocations = [] } = useAnatomicalLocations(
    (values.protocol?.protocolDiagnoses || [])[0]?.medicalConditionId || '',
  )

  useEffect(() => {
    if (values.protocol && anatomicalLocations) {
      const pLocations = anatomicalLocations.filter((al) =>
        (
          (values.protocol?.protocolDiagnoses || [])[0]?.locations || []
        ).includes(al.name),
      )
      setProtocolAnatomicalLocations(pLocations || [])
    }
  }, [values.protocol])

  return (
    <Box className={classes.root}>
      <Typography className={classes.title}>Select Body Part</Typography>
      <form
        className={classes.form}
        onSubmit={handleSubmit}
        id="patient-flow-add-case-form"
      >
        {(availableStudies.length > 0 ||
          (protocols && protocols.length > 0)) && (
          <ToggleButtonGroup
            id="patient-self-onboard-add-case-enrollment-toggle"
            color="primary"
            value={values.enrollment}
            exclusive
            fullWidth
            onChange={(_event, s) => {
              setFieldValue('enrollment', s)
              if (s === 'standard') {
                const noneStudy = studies.find((s) => s.slug === 'none')
                setFieldValue('studyId', noneStudy?.id || '')
                setFieldValue('bodySiteIds', [])
                setFieldValue('protocol', null)
                updateBodySitesOptions(noneStudy || null)
              } else {
                setFieldValue('studyId', '')
                setFieldValue('bodySiteIds', [])
                setFieldValue('protocol', null)
                updateBodySitesOptions(null)
              }
            }}
            aria-label="Platform"
          >
            <ToggleButton
              id="patient-self-onboard-add-case-enrollment-standard"
              value="standard"
            >
              <Box>
                Standard <br />
                <span>General Care</span>
              </Box>
            </ToggleButton>
            <ToggleButton
              id="patient-self-onboard-add-case-enrollment-study"
              value="study"
            >
              <Box>
                {isContura ? 'Protocol' : 'Study'}
                <br />
                <span>{isContura ? '' : 'Research Participant'}</span>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        {values.enrollment === 'study' && (
          <Autocomplete
            id="patient-self-onboard-study-protocol-selection"
            options={[...availableStudies, ...(protocols || [])]}
            getOptionLabel={(opt) =>
              'shortName' in opt ? opt.shortName : opt.name
            }
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            filterOptions={(options, { inputValue }) =>
              inputValue
                ? matchSorter(options, inputValue, {
                    keys: [
                      (opt) => ('shortName' in opt ? opt.shortName : opt.name),
                      'name',
                    ],
                  })
                : options
            }
            value={
              values.studyId
                ? studies.find((s) => s.id === values.studyId) || null
                : values.protocol || null
            }
            fullWidth
            onChange={(_event, data) => {
              if (!data) {
                // Clear button pressed
                setFieldValue('studyId', '')
                setFieldValue('protocol', null)
                setFieldValue('bodySiteIds', [])
                updateBodySitesOptions(null)
              } else if (
                data &&
                availableStudies.find((s) => s.id === data.id)
              ) {
                // It's a study
                setFieldValue('studyId', data.id)
                setFieldValue('protocol', null)
                setFieldValue('bodySiteIds', [])
                updateBodySitesOptions(data as Study)
              } else if (data) {
                // It's a protocol
                setFieldValue('protocol', data as Protocol)
                setFieldValue('studyId', '')
                setSearchValue('')
                const protocol = data as Protocol
                const protocolBodySite = availableBodySites.find(
                  (bs) => bs.id === protocol?.bodySite?.id,
                )
                if (protocolBodySite) {
                  setAvailableBodySitesOptions([protocolBodySite])
                  setFieldValue('bodySiteIds', [protocolBodySite.id])
                } else {
                  setAvailableBodySitesOptions([])
                  setFieldValue('bodySiteIds', [])
                  setFieldError(
                    'protocol',
                    'Body site of this protocol not available',
                  )
                }
              }
            }}
            renderInput={(params) => {
              // @ts-expect-error Mui's autocomplete types here are incomplete
              params.InputLabelProps.shrink = true
              return (
                <TextField
                  {...params}
                  label={isContura ? 'Protocol' : 'Study or Protocol'}
                />
              )
            }}
          />
        )}

        {/* {values.enrollment === 'study' && ( */}
        <Autocomplete
          id="patient-selfonboarding-flow-study-treatment-provider"
          options={providers}
          getOptionLabel={(opt) => buildName(opt)}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          filterOptions={(options, { inputValue }) =>
            inputValue
              ? matchSorter(options, inputValue, {
                  keys: ['firstName', 'lastName'],
                })
              : options
          }
          disabled={
            values.protocol && !values.protocol.organization?.id ? true : false
          }
          value={
            providers.find((prov) => prov.id === values.providerId) ?? null
          }
          onChange={(_event, prov) => {
            setFieldValue('providerId', prov?.id || '')
          }}
          renderInput={(params) => {
            // @ts-expect-error Mui's autocomplete types here are incomplete
            params.InputLabelProps.shrink = true
            return (
              <TextField
                {...params}
                name="providerId"
                label="Provider"
                onBlur={handleBlur}
                required
                value={values.providerId}
                error={touched.providerId && Boolean(errors.providerId)}
                helperText={touched.providerId && errors.providerId}
              />
            )
          }}
        />
        {/* )} */}
        {values.enrollment === 'standard' && !values.protocol && (
          <TextField
            className={classes.searchField}
            placeholder={'Search'}
            onChange={(e) => setSearchValue(e.target.value)}
            disabled={values.protocol ? true : false}
            value={searchValue}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search className={classes.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
        )}
        {((values.enrollment === 'standard' && !values.protocol) ||
          (values.enrollment === 'study' && values.studyId) ||
          values.protocol) && (
          <FormikProvider value={formik}>
            <FieldArray
              name="bodySiteIds"
              render={() => (
                <Grid container spacing={2}>
                  {availableBodySitesOptions.map((bodySite) => (
                    <Grid
                      item
                      key={bodySite.id}
                      onClick={() => {
                        const index = values.bodySiteIds.findIndex(
                          (x) => x == bodySite.id,
                        )
                        if (index > -1) {
                          setFieldValue('bodySiteIds', [])
                        } else {
                          setFieldValue('bodySiteIds', [bodySite.id])
                        }
                      }}
                    >
                      <MuiStack
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        className={clsx(classes.bodyPartCard, {
                          [classes.activeBodyPartCard]: values.bodySiteIds.includes(
                            bodySite.id,
                          ),
                          [classes.disabledBodyPartCard]: values.protocol
                            ? true
                            : false,
                        })}
                      >
                        <Avatar className={classes.avatar}>
                          <img
                            src={`${process.env.PUBLIC_URL}/assets/bodyIcons/${bodySite.slug}.svg`}
                            className={classes.avatarIcon}
                          />
                        </Avatar>
                        <Typography
                          className={clsx(classes.bodyPartText, {
                            [classes.activeBodyPartText]: values.bodySiteIds.includes(
                              bodySite.id,
                            ),
                          })}
                        >
                          {bodySite.name}
                        </Typography>
                      </MuiStack>
                    </Grid>
                  ))}
                </Grid>
              )}
            />
          </FormikProvider>
        )}
        <MuiStack
          direction="row"
          justifyContent="center"
          sx={{
            marginTop: '40px',
            marginBottom: '40px',
            paddingBottom: '40px',
          }}
        >
          <Button
            className={classes.goBackButton}
            disableElevation
            fullWidth
            style={{ marginRight: '20px' }}
            onClick={goBack}
          >
            Go Back
          </Button>
          {/* TODO: change submit form to validation and change step */}
          <Button
            variant="contained"
            disableElevation
            fullWidth
            disabled={!isValid}
            loading={isSubmitting}
            onClick={() => submitForm()}
          >
            Continue
          </Button>
        </MuiStack>
      </form>
    </Box>
  )
}

export default AddCase
